import { useEffect, useRef, useState, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { useAuth } from 'react-oidc-context';
import { ENV } from '../config/env';
import { alarmAcknowledgedEvent } from '../services/events/alarmEvents';

export interface BureauAlarmNotification {
  bureauAlarmId: number;
  siteIdentifierRegistryId: number;
  alarmPathId: number;
  isVisible: boolean;
  bureauAlarmState: string;
  priority: number | null;
  occurredAt: string;
}

/**
 * Hook that manages a SignalR connection to the CSH group hub.
 * Provides real-time BureauAlarmUpdated notifications for the current user's groups.
 * The server-side CshGroupHub automatically joins the connection to the correct groups
 * based on the authenticated user's identity (CshGroupUser assignments for regular users,
 * all active groups for admins).
 */
export function useBureauSignalR() {
  const auth = useAuth();
  const [lastNotification, setLastNotification] = useState<BureauAlarmNotification | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const notifyCallbacksRef = useRef<Set<(n: BureauAlarmNotification) => void>>(new Set());

  const subscribe = useCallback((callback: (n: BureauAlarmNotification) => void) => {
    notifyCallbacksRef.current.add(callback);
    return () => { notifyCallbacksRef.current.delete(callback); };
  }, []);

  useEffect(() => {
    if (!auth.user?.access_token) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(ENV.SIGNALR_HUB_URL, {
        accessTokenFactory: () => auth.user!.access_token
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    connection.on('BureauAlarmUpdated', (notification: BureauAlarmNotification) => {
      setLastNotification(notification);
      alarmAcknowledgedEvent.emit({ source: 'signalr', eventType: 'BureauAlarmUpdated' });
      notifyCallbacksRef.current.forEach(cb => cb(notification));
    });

    connection.on('CustomerAlarmTreeUpdated', (data: { customerId: number }) => {
      alarmAcknowledgedEvent.emit({ source: 'signalr', eventType: 'CustomerAlarmTreeUpdated' });
    });

    connection.onreconnected(() => setIsConnected(true));
    connection.onclose(() => setIsConnected(false));

    connection.start()
      .then(() => setIsConnected(true))
      .catch(err => console.info('SignalR connection failed:', err.message));

    return () => {
      connection.stop();
    };
  }, [auth.user?.access_token]);

  return { lastNotification, isConnected, subscribe };
}
